"use client";

import { useState } from "react";
import Image from "next/image";
import { storyblokEditable } from "@storyblok/react";
import clsx from "clsx";
import { PageBannerBlok } from "@/types/sb-types";

interface PageBannerProps {
  blok: PageBannerBlok;
}

const PageBanner = ({ blok }: PageBannerProps) => {
  const { label, title, image, mobile_image } = blok;
  const hasMobileImage = !!mobile_image?.filename;
  const [isLoaded, setIsLoaded] = useState(false);

  return (
    <section
      className="relative isolate flex h-70 xl:h-95 w-full flex-col items-center justify-center gap-6 xl:gap-10 overflow-hidden px-5 md:px-12.5 text-center"
      {...storyblokEditable(blok)}
    >
      {!isLoaded && (
        <div
          className="absolute inset-0 -z-30 animate-pulse bg-tm-c-gray-800"
          aria-hidden
        />
      )}
      {hasMobileImage && (
        <Image
          src={mobile_image.filename}
          alt={mobile_image.alt || ""}
          fill
          sizes="100vw"
          onLoad={() => setIsLoaded(true)}
          className="-z-20 object-cover block md:hidden"
        />
      )}
      {image?.filename && (
        <Image
          src={image.filename}
          alt={image.alt || ""}
          fill
          sizes="100vw"
          quality={100}
          onLoad={() => setIsLoaded(true)}
          className={clsx("-z-20 object-cover", {
            "hidden md:block": hasMobileImage,
          })}
        />
      )}
      <div className="absolute inset-0 -z-10 bg-black/50" aria-hidden />

      <div className="flex flex-col items-center gap-2 md:gap-4">
        {label && (
          <span className="text-xs font-medium uppercase">{label}</span>
        )}
        {title && (
          <h1 className="m-0 text-2xl md:text-4xl 2xl:text-5xl">{title}</h1>
        )}
      </div>

      <span
        aria-hidden
        className="block w-full h-0.5 bg-linear-to-r from-[rgba(21,20,20,0)] via-tm-c-brand to-[rgba(21,20,20,0)]"
      />
    </section>
  );
};

export default PageBanner;
